// @ts-strict-ignore
/** @jsx jsx */
import { jsx } from 'theme-ui'
import { useForm } from 'react-hook-form'
import { InputAlert } from '@uswitch/ev-components/lib/components/elements/input-alert'
import { SimpleInput } from '@uswitch/ev-components/lib/components/elements/simple-input'
import { Button } from '@uswitch/ev-components/lib/components/elements/trustyle-button'

const selectStyle = {
  boxSizing: 'border-box',
  borderRadius: '4px',
  borderColor: '#ADAFB3',
  fontFamily: 'base',
  fontSize: 'xxxs',
  height: '55px',
  lineHeight: '24px',
}

type VerificationFormProps = {
  providerName?: string
  verifyDOB: (dob: string) => void
  brand: string
  dobWrong?: boolean
  heading?: string
  subheading?: string
  skip?: () => void
}

const ErrorMsg = ({ msg }: { msg: string }) => {
  return (
    <InputAlert
      type='alert'
      sx={{ top: '16px', variant: 'elements.form.variants.default.input.error' }}
    >
      {msg}
    </InputAlert>
  )
}

const VerificationForm = ({
  providerName,
  verifyDOB,
  brand,
  dobWrong,
  heading = 'Your session timed out',
  subheading = "We need to make sure it's you and keep your data safe",
  skip,
}: VerificationFormProps) => {
  const formClick = (d) => {
    d.dateOfBirthDay &&
      d.dateOfBirthMonth &&
      d.dateOfBirthYear &&
      verifyDOB(`${d.dateOfBirthDay}${d.dateOfBirthMonth}${d.dateOfBirthYear}`)
  }
  const { register, handleSubmit, errors } = useForm({ mode: 'onBlur' })
  return (
    <div
      sx={{
        backgroundColor: brand === 'money' ? 'secondary' : '',
        height: '75vh',
        marginLeft: ['-16px', 'calc(50% - 50vw)'],
        marginRight: ['-16px', 'calc(50% - 50vw)'],
      }}
    >
      <div
        sx={{
          display: 'block',
        }}
      >
        <div
          sx={{
            backgroundColor: '#fff',
            width: ['auto', '560px'],
            padding: '32px 24px',
            margin: ['0 16px', '60px auto'],
          }}
        >
          <div
            sx={{
              color: brand === 'money' ? 'secondary' : 'primary',
              fontFamily: 'heading',
              fontWeight: '600',
              fontSize: '24px',
              lineHeight: '120%',
              margin: '16px 0px',
            }}
          >
            {heading}
          </div>
          <div
            sx={{
              fontFamily: 'heading',
              fontWeight: 'base',
              fontSize: '20px',
              lineHeight: '24px',
              margin: '16px 0px',
            }}
          >
            {subheading}
          </div>
          <div
            sx={{
              fontFamily: 'base',
              fontWeight: 'bold',
              fontSize: '16px',
              lineHeight: '120%',
              margin: '8px 0px',
            }}
          >
            Date of birth
          </div>

          <form onSubmit={handleSubmit(formClick)}>
            <div sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
              <div sx={{ ...selectStyle, width: ['29%', '30%'] }}>
                <SimpleInput
                  name='dateOfBirthDay'
                  ref={register({
                    required: true,
                    pattern: /^(0[1-9]|[12][0-9]|3[01])$/,
                  })}
                  placeholder='DD'
                  hasError={!!errors.dateOfBirthDay}
                />
              </div>

              <div sx={{ ...selectStyle, width: ['33%', '30%'] }}>
                <SimpleInput
                  name='dateOfBirthMonth'
                  ref={register({ required: true, pattern: /^(0[1-9]|1[012])$/ })}
                  placeholder='MM'
                  hasError={!!errors.dateOfBirthMonth}
                />
              </div>

              <div sx={{ ...selectStyle, width: ['34%', '30%'] }}>
                <SimpleInput
                  name='dateOfBirthYear'
                  ref={register({ required: true, pattern: /^(\d{4})$/ })}
                  placeholder='YYYY'
                  hasError={!!errors.dateOfBirthYear}
                />
              </div>
            </div>
            {(errors.dateOfBirthDay || errors.dateOfBirthMonth || errors.dateOfBirthYear) && (
              <ErrorMsg msg={'Please complete above fields in the correct format'} />
            )}
            {dobWrong && <ErrorMsg msg={'Please enter correct date of birth'} />}
            <div sx={{ display: 'flex', justifyContent: 'center' }}>
              <Button
                type='submit'
                variant={brand === 'money' ? 'secondary' : 'primary'}
                sx={{
                  fontWeight: '600',
                  fontSize: '16px',
                  lineHeight: '140%',
                  height: '48px',
                  width: '300px',
                  marginTop: '40px',
                  padding: 0,
                  '&:hover': {
                    backgroundColor: '#5D6A72',
                    cursor: 'pointer',
                  },
                }}
              >
                {providerName ? `Continue to ${providerName}` : 'Get my details'}
              </Button>
            </div>
          </form>
          {skip && (
            <div
              sx={{
                textAlign: 'center',
                marginTop: '10px',
              }}
            >
              <button
                sx={{
                  textDecoration: 'underline',
                  '&:hover': {
                    cursor: 'pointer',
                  },
                }}
                onClick={skip}
              >
                No thanks, I&apos;ll enter my details again
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default VerificationForm
